import React from "react";
import { AnimatePresence } from "framer-motion";
import { useDocumentEditor } from "../onboardingHooks/useDocumentEditor";
import { DocumentFieldsSidebar } from "./DocumentFieldsSidebar";
import { DocumentFieldConfig } from "./DocumentFieldConfig";
import { DraggableField } from "../fields/Draggablefield";
import { CanvasPageRenderer } from "./CanvasPageRenderer";
import { A4_WIDTH, A4_HEIGHT } from "../constants/layoutConstants";
import Button from "../../Buttons/Button";
export const DocumentEditor = ({ document, onSave, onBack }) => {
  const pageCount = document.pageCount || 1;
  console.log({ document });

  const {
    currentPageNum,
    setCurrentPageNum,
    droppedFields,
    selectedField,
    setSelectedField,
    draggedFieldType,
    setDraggedFieldType,
    previewRef,
    addField,
    deleteField,
    updateFieldAttribute,
    updateFieldPosition,
  } = useDocumentEditor(pageCount, document.droppedFields);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedFieldType || !currentPageNum) return;

    const rect = previewRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left - 75, A4_WIDTH - 150));
    const y = Math.max(0, Math.min(e.clientY - rect.top - 18, A4_HEIGHT - 36));

    addField(draggedFieldType, x, y);
    setDraggedFieldType(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleSave = () => {
    const hasFields = Object.values(droppedFields).some(
      (fields) => fields.length > 0
    );

    if (hasFields) {
      onSave({
        ...document,
        pageCount,
        droppedFields,
      });
    } else {
      alert("Please add at least one field before saving.");
    }
  };

  const selectedFieldData = selectedField
    ? droppedFields[currentPageNum]?.find((f) => f.id === selectedField)
    : null;

  return (
    <div className="flex flex-1 flex-col bg-gray-100">
      {/* Header - Fixed */}
      <div className="bg-white border-b px-2 py-1 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <Button
            type="icon"
            icon={"chevron_left"}
            variant="text"
            color="black"
            onClick={onBack}
          />
          <div>
            <h2 className="text-xl font-semibold">
              {document.documentName || "Untitled Document"}
            </h2>
            <p className="text-sm text-gray-500">Design Document</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button title={"Save & share"} onClick={handleSave} />
        </div>
      </div>

      <div className="flex flex-1">
        <DocumentFieldsSidebar onDragStart={setDraggedFieldType} />

        <div className="flex-1 flex flex-col overflow-hidden p-8">
          <div className="flex justify-center">
            <div className="w-full max-w-4xl">
              <div className="flex items-center justify-between mb-4">
                <Button
                  type="icon"
                  icon={"chevron_left"}
                  onClick={() =>
                    setCurrentPageNum((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPageNum === 1}
                  variant="text"
                  color="black"
                />
                <span className="text-sm font-medium">
                  Page {currentPageNum} of {pageCount}
                </span>
                <Button
                  type="icon"
                  icon={"chevron_right"}
                  variant="text"
                  color="black"
                  onClick={() =>
                    setCurrentPageNum((prev) => Math.min(pageCount, prev + 1))
                  }
                  disabled={currentPageNum === pageCount}
                />
              </div>

              {/* Document Canvas */}
              <div
                ref={previewRef}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className="relative bg-white shadow-lg mx-auto mb-8"
                style={{
                  width: `${A4_WIDTH}px`,
                  height: `${A4_HEIGHT}px`,
                  position: "relative",
                }}
              >
                {/* Background Layer */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 0,
                    pointerEvents: "none",
                  }}
                >
                  <CanvasPageRenderer
                    arrayBuffer={document.arrayBuffer}
                    pageNumber={currentPageNum}
                    width={A4_WIDTH}
                    height={A4_HEIGHT}
                    isBlankDocument={document.isBlankDocument}
                  />
                </div>

                {/* Fields Layer */}
                <div style={{ position: "absolute", inset: 0, zIndex: 10 }}>
                  <AnimatePresence>
                    {(droppedFields[currentPageNum] || []).map((field) => (
                      <DraggableField
                        key={field.id}
                        field={field}
                        isSelected={selectedField === field.id}
                        onSelect={() => setSelectedField(field.id)}
                        onUpdatePosition={updateFieldPosition}
                        onDelete={deleteField}
                        mode="editor"
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Scrollable */}
        <DocumentFieldConfig
          field={selectedFieldData}
          onUpdate={updateFieldAttribute}
          onClose={() => setSelectedField(null)}
        />
      </div>
    </div>
  );
};
